import { HELPERS } from '@/utils/helpers';
import { Icon } from '@iconify/react';
import React from 'react';

const ErrorModalUI = ({
  title,
  titleBadgeText,
  titleBadgeColors,
  message,
  iconName,
  iconContainerClass,
  iconClass,
  gradientClass,
  children,
}) => (
  <div className="fixed inset-0 bg-black/0 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden animate-fade-in">
      <div className="relative overflow-hidden">
        {/* Top decorative gradient */}
        <div className={`absolute top-0 inset-x-0 h-1 ${gradientClass}`}></div>

        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`${iconContainerClass} p-3 rounded-full`}>
              <Icon icon={iconName} width="24" height="24" className={iconClass} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <span
                  className={`inline-flex items-center rounded-full border ${titleBadgeColors.border} ${titleBadgeColors.bg} px-2.5 py-0.5 text-xs font-normal ${titleBadgeColors.text}`}
                >
                  {titleBadgeText}
                </span>
              </div>
              <p className="text-gray-600 text-sm">{message}</p>
            </div>
          </div>
        </div>
        <hr className="border-gray-200" />
        {children}
      </div>
    </div>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    // Anda bisa mengirim error ke layanan logging di sini
    // Sentry.captureException(error, { extra: errorInfo });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showErrorDetails: false });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  toggleErrorDetails = () => {
    this.setState((prevState) => ({
      showErrorDetails: !prevState.showErrorDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, showErrorDetails } = this.state;
      const { showDetailsInProd = false } = this.props;
      const showDetailsEnabled = process.env.NODE_ENV === 'development' || showDetailsInProd;

      let isUpdateError = false;
      const errorMessageLc = error?.message?.toLowerCase() || '';
      const errorNameLc = error?.name?.toLowerCase() || '';

      if (
        errorNameLc === 'chunkloaderror' ||
        errorMessageLc.includes('failed to fetch dynamically imported module') ||
        errorMessageLc.includes('loading chunk') ||
        errorMessageLc.includes('importing a module script')
      ) {
        isUpdateError = true;
      }

      const technicalDetailsSection = (
        <div className="rounded-md bg-gray-50 border border-gray-200">
          <button
            onClick={this.toggleErrorDetails}
            className="w-full px-4 py-2 text-sm flex items-center justify-between text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span>Detail Teknis</span>
            <Icon icon={showErrorDetails ? 'mdi:chevron-up' : 'mdi:chevron-down'} width="16" height="16" />
          </button>

          {showErrorDetails && (
            <div className="px-4 pb-3">
              <p className="text-sm font-medium text-red-600 mb-1">
                {error?.message || 'Terjadi kesalahan yang tidak diketahui'}
              </p>
              {errorInfo && (
                <pre className="text-xs overflow-auto p-2 bg-gray-100 rounded-sm max-h-32 text-gray-600">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          )}
        </div>
      );

      const actionButtons = (
        <div className="p-6 pt-2 flex flex-col gap-2">
          <button
            onClick={this.handleRefresh}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md font-medium flex items-center justify-center"
          >
            Muat Ulang Halaman
            <Icon icon="mdi:refresh" className="ml-2" width="16" height="16" />
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => (window.location.href = '/')}
              className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 px-4 rounded-md font-medium flex items-center justify-center"
            >
              <Icon icon="mdi:home" className="mr-2" width="16" height="16" />
              Beranda
            </button>
            <button
              onClick={() => HELPERS.handleClickWhatsapp()} // Pastikan HELPERS.handleClickWhatsapp ada
              className="flex-1 text-purple-600 hover:text-purple-700 py-2 px-4 rounded-md font-medium flex items-center justify-center"
            >
              Butuh Bantuan?
              <Icon icon="mdi:arrow-right" className="ml-1" width="16" height="16" />
            </button>
          </div>
        </div>
      );

      if (isUpdateError) {
        return (
          <ErrorModalUI
            title="Pembaruan Diperlukan"
            titleBadgeText="Sistem"
            titleBadgeColors={{
              border: 'border-purple-200',
              bg: 'bg-purple-50',
              text: 'text-purple-600',
            }}
            message="Kami baru saja melakukan pembaruan pada aplikasi. Silakan muat ulang halaman untuk mendapatkan versi terbaru."
            iconName="mdi:alert-circle-outline"
            iconContainerClass="bg-purple-100"
            iconClass="text-purple-600"
            gradientClass="bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-500"
          >
            <div className="p-6 pt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Icon icon="mdi:refresh" width="16" height="16" />
                  <span>Muat ulang halaman untuk mendapatkan versi terbaru.</span>
                </div>
                {showDetailsEnabled && technicalDetailsSection}
              </div>
            </div>
            {actionButtons}
          </ErrorModalUI>
        );
      } else {
        // Tampilan untuk error umum
        return (
          <ErrorModalUI
            title="Terjadi Kesalahan"
            titleBadgeText="Aplikasi"
            titleBadgeColors={{
              border: 'border-red-200',
              bg: 'bg-red-50',
              text: 'text-red-600',
            }}
            message="Maaf, aplikasi mengalami masalah yang tidak terduga. Tim kami telah diberitahu. Coba muat ulang halaman."
            iconName="mdi:alert-octagon-outline"
            iconContainerClass="bg-red-100"
            iconClass="text-red-600"
            gradientClass="bg-gradient-to-r from-red-500 via-red-600 to-pink-500"
          >
            <div className="p-6 pt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Icon icon="mdi:information-outline" width="16" height="16" />
                  <span>Jika masalah berlanjut, silakan hubungi dukungan atau coba lagi nanti.</span>
                </div>
                {showDetailsEnabled && technicalDetailsSection}
              </div>
            </div>
            {actionButtons}
          </ErrorModalUI>
        );
      }
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
